import {
  getOperationsControllerGetOperationStationsQueryKey,
  getOperationsControllerGetOperationStationsQueryOptions,
  operationsControllerCommitStationsDraft,
  operationsControllerCreateOperation,
  operationsControllerGetOperationDetail,
  operationsControllerListBillsOfOperationsForOperation,
  operationsControllerListOperationCategories,
  operationsControllerListOperationReleaseTargets,
  operationsControllerListOperations,
  operationsControllerListOperationSubCategories,
  operationsControllerListRulesForOperation,
  operationsControllerReorderOperations,
  operationsControllerSaveChecksDraft,
  operationsControllerSaveDetailsDraft,
  operationsControllerSaveStationsDraft,
  operationsControllerUpdateOperation,
  type OperationChecksDraftUpdateDto,
  type OperationDetailsDraftUpdateDto,
  type OperationStationDto,
  type OperationStationsDraftUpdateDto,
  type OperationUpdateDto,
  type OperationsControllerListOperationsParams,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { CHANGE_CONTROL_KEY } from '../change-control/queries';
import { JOB_CARDS_KEY } from '../job-cards/queries';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import type {
  RunSheetsUsage,
  OperationCategory,
  OperationCreateInput,
  OperationDetail,
  OperationDetailsDraftInput,
  OperationInProcessCheck,
  OperationListItem,
  OperationPreFlightCheck,
  OperationReleaseTarget,
  OperationRelevanceRule,
  OperationStationToggle,
  OperationSubCategory,
  OperationUpdateInput,
} from './types';

export const OPERATIONS_KEY = 'operations' as const;

// ── List ──────────────────────────────────────────────────────────────

export function operationsListQueryOptions(
  partnerId: string | undefined,
  filters: Omit<OperationsControllerListOperationsParams, 'partner_id'> = {},
  enabled = true,
) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'list', partnerId, filters] as const,
    queryFn: async ({ signal }): Promise<OperationListItem[]> => {
      const res = await operationsControllerListOperations(
        { partner_id: partnerId ?? '', ...filters },
        { signal },
      );
      return expectData<OperationListItem[]>(res);
    },
    enabled: !!partnerId && enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Detail ────────────────────────────────────────────────────────────

export function operationDetailQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'detail', operationId] as const,
    queryFn: async ({ signal }): Promise<OperationDetail> => {
      const res = await operationsControllerGetOperationDetail(operationId ?? '', { signal });
      return expectData<OperationDetail>(res);
    },
    enabled: !!operationId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Bills of Operations using the operation ──────────────────────────

export function operationRunSheetsQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'run-sheets', operationId] as const,
    queryFn: async ({ signal }): Promise<RunSheetsUsage[]> => {
      const res = await operationsControllerListBillsOfOperationsForOperation(operationId ?? '', {
        signal,
      });
      return expectData<RunSheetsUsage[]>(res);
    },
    enabled: !!operationId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * The Bills of Operations a release of this operation should target right
 * now — including ones it would newly join, given its CURRENT staged Rules.
 * Powers the ENG-813 Operation-release wizard; NOT the same set as
 * {@link operationRunSheetsQueryOptions}, which only reports Bills of
 * Operations whose released version already lists the operation.
 */
export function operationReleaseTargetsQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'release-targets', operationId] as const,
    queryFn: async ({ signal }): Promise<OperationReleaseTarget[]> => {
      const res = await operationsControllerListOperationReleaseTargets(operationId ?? '', {
        signal,
      });
      return expectData<OperationReleaseTarget[]>(res);
    },
    enabled: !!operationId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

// ── Categories ────────────────────────────────────────────────────────

export function operationCategoriesQueryOptions() {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'categories'] as const,
    queryFn: async ({ signal }): Promise<OperationCategory[]> => {
      const res = await operationsControllerListOperationCategories({ signal });
      return expectData<OperationCategory[]>(res);
    },
    // Categories rarely change; cache for the whole session.
    staleTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ── Sub-categories ────────────────────────────────────────────────────

/**
 * Sub-categories under a chosen category. The form's cascading
 * sub-category dropdown calls this when the category changes. When
 * `category` is null/undefined, the backend returns the full list.
 */
export function operationSubCategoriesQueryOptions(category: string | null | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'sub-categories', category ?? null] as const,
    queryFn: async ({ signal }): Promise<OperationSubCategory[]> => {
      const res = await operationsControllerListOperationSubCategories(
        category ? { operation_category: category } : undefined,
        { signal },
      );
      return expectData<OperationSubCategory[]>(res);
    },
    staleTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ── Rules targeting this operation ───────────────────────────────────

/**
 * Relevance rules whose `operation_id` matches. Powers the "Rules"
 * panel on the operation detail page.
 */
export function operationRulesQueryOptions(operationId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATIONS_KEY, 'rules', operationId] as const,
    queryFn: async ({ signal }): Promise<OperationRelevanceRule[]> => {
      const res = await operationsControllerListRulesForOperation(operationId ?? '', { signal });
      return expectData<OperationRelevanceRule[]>(res);
    },
    enabled: !!operationId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ── Create ────────────────────────────────────────────────────────────

export function useCreateOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: OperationCreateInput) => {
      const res = await operationsControllerCreateOperation(input);
      return expectData<{ operation_id: string }>(res);
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'list', input.partner_id] });
    },
  });
}

// ── Reorder (admin only) ─────────────────────────────────────────────

interface ReorderOperationsArgs {
  partnerId: string;
  operationIds: string[];
}

/**
 * Admin-only: reassigns `operation_sequence` across the partner's
 * operations to match the supplied ordered array. Optimistically
 * rewrites the cached list with the new sequence values then rolls
 * back on error. Invalidates `bill_of_operations` because the matrix
 * sorts ops by `operation_sequence` too.
 */
export function useReorderOperations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ partnerId, operationIds }: ReorderOperationsArgs) => {
      await operationsControllerReorderOperations({
        partner_id: partnerId,
        operation_ids: operationIds,
      });
    },
    onMutate: async ({ partnerId, operationIds }) => {
      // Cancel any in-flight list refetches for this partner so they
      // don't overwrite the optimistic state.
      await queryClient.cancelQueries({
        queryKey: [OPERATIONS_KEY, 'list', partnerId],
      });
      // Snapshot every cached list query for this partner (search/archive
      // variants live under the same prefix) so onError can restore each.
      const snapshots = queryClient.getQueriesData<OperationListItem[]>({
        queryKey: [OPERATIONS_KEY, 'list', partnerId],
      });
      const orderMap = new Map(operationIds.map((id, i) => [id, i + 1]));
      for (const [key, data] of snapshots) {
        if (!data) continue;
        const updated = data
          .map((row) =>
            orderMap.has(row.operation_id)
              ? {
                  ...row,
                  operation_sequence: orderMap.get(row.operation_id) ?? row.operation_sequence,
                }
              : row,
          )
          .slice()
          .sort((a, b) => a.operation_sequence - b.operation_sequence);
        queryClient.setQueryData(key, updated);
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: (_data, _err, { partnerId }) => {
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'list', partnerId] });
      // Matrix sorts within each category by operation_sequence too.
      void queryClient.invalidateQueries({ queryKey: ['run_sheets'] });
    },
  });
}

// ── Update ────────────────────────────────────────────────────────────

interface UpdateOperationArgs {
  operationId: string;
  partnerId: string | undefined;
  input: OperationUpdateInput;
}

export function useUpdateOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ operationId, input }: UpdateOperationArgs) => {
      // Backend always cascades the update to Bill of Operations versions
      // via the `update_bills_of_operations: true` event flag — no client
      // option needed.
      //
      // `OperationUpdateInput` is the quality-side shape; it differs from the
      // generated `OperationUpdateDto` only in the structural typing of the
      // nested check items (the wire payload is identical). Cast to the DTO
      // the generated function expects.
      await operationsControllerUpdateOperation(operationId, input as OperationUpdateDto);
    },
    onSuccess: (_data, { operationId, partnerId, input }) => {
      // Invalidate detail (new version), list (version + counts), and
      // Run Sheet usage (new Run Sheet versions cascaded by the event handler).
      // Also invalidate the Run Sheet matrix for the partner since the cascade
      // bumps Bills of Operations referenced by this operation.
      void queryClient.invalidateQueries({
        queryKey: [OPERATIONS_KEY, 'detail', operationId],
      });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'list', partnerId] });
      void queryClient.invalidateQueries({
        queryKey: [OPERATIONS_KEY, 'run-sheets', operationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['run_sheets'] });
      // Product detail pages keyed by ['products', 'run-sheets', productId]
      // also surface the operation_version + applied_work_time / applied_headcount
      // for this operation. Invalidate the prefix so each product Run Sheet query
      // re-fetches on next mount.
      void queryClient.invalidateQueries({ queryKey: ['products', 'run-sheets'] });
      // A commit changes which active work orders are behind, so refresh the
      // Change Control behind-work-orders set that drives the release affordance.
      void queryClient.invalidateQueries({
        queryKey: [CHANGE_CONTROL_KEY, 'behind-work-orders'],
      });

      if (input.is_archived === true) {
        // Archiving queues a backend cascade that cuts an operation-free Run
        // Sheet version and clears this operation's scheduled job cards from
        // Scheduled / Redrafting work orders. That work happens on a BullMQ
        // queue AFTER this response, so refetching now would just re-read the
        // pre-cascade state.
        //
        // `refetchType: 'none'` marks the job-card caches stale without
        // refetching: mounted planner grids don't thrash for a result that
        // isn't ready, and the next mount / window refocus picks up the
        // post-cascade truth. Card DELETEs emit no realtime event
        // (job_cards_artic_realtime_notify is AFTER INSERT OR UPDATE only), so
        // this staleness marker is what makes those views recover.
        void queryClient.invalidateQueries({
          queryKey: [PRODUCTION_PLANNER_KEY],
          refetchType: 'none',
        });
        void queryClient.invalidateQueries({
          queryKey: [JOB_CARDS_KEY],
          refetchType: 'none',
        });
        // Run history gains a new run for the cascade.
        void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'runs'] });
      }
    },
  });
}

// ── Checks autosave draft (ENG-764) ──────────────────────────────────

interface SaveOperationChecksDraftArgs {
  operationId: string;
  input: {
    operation_pre_flight_checks: OperationPreFlightCheck[];
    operation_in_process_checks: OperationInProcessCheck[];
  };
}

/**
 * Debounced-autosave target for the Checks editor: persists straight to
 * `operations.draft_*` (see the ENG-764 migration), never bumping
 * `operation_version` or cascading to Bills of Operations the way
 * `useUpdateOperation` does. Optimistically writes the draft fields onto the
 * cached operation detail so the floating bar's "Saved Xs ago" reflects the
 * save immediately; reconciles with the server's authoritative
 * `draft_saved_at` on success and rolls back on error.
 */
export function useSaveOperationChecksDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ operationId, input }: SaveOperationChecksDraftArgs) => {
      const res = await operationsControllerSaveChecksDraft(
        operationId,
        input as OperationChecksDraftUpdateDto,
      );
      return expectData<{ draft_saved_at: string }>(res);
    },
    onMutate: async ({ operationId, input }) => {
      const key = [OPERATIONS_KEY, 'detail', operationId] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OperationDetail>(key);
      queryClient.setQueryData<OperationDetail>(key, (old) =>
        old
          ? {
              ...old,
              draft_pre_flight_checks: input.operation_pre_flight_checks,
              draft_in_process_checks: input.operation_in_process_checks,
              draft_saved_at: new Date().toISOString(),
            }
          : old,
      );
      return { previous, key };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSuccess: (data, { operationId }) => {
      // Reconcile the optimistic timestamp with the server's authoritative one.
      const key = [OPERATIONS_KEY, 'detail', operationId] as const;
      queryClient.setQueryData<OperationDetail>(key, (old) =>
        old ? { ...old, draft_saved_at: data.draft_saved_at } : old,
      );
    },
  });
}

// ── Details form autosave draft ──────────────────────────────────────

interface SaveOperationDetailsDraftArgs {
  operationId: string;
  input: OperationDetailsDraftInput;
}

/**
 * Debounced-autosave target for the Details form: persists straight to
 * `operations.draft_*` — the same reused ENG-764 draft slot the Checks/
 * Stations drafts use — never bumping `operation_version` or cascading to
 * Bills of Operations. There is no separate commit endpoint for this draft
 * kind: the floating bar's "Commit Changes" action sends `draft_details`
 * straight to `useUpdateOperation` (`PATCH /operations/:id`), which already
 * clears this draft slot unconditionally. Optimistically writes the draft
 * fields onto the cached operation detail so the floating bar's "Saved Xs
 * ago" reflects the save immediately; reconciles with the server's
 * authoritative `draft_saved_at` on success and rolls back on error.
 */
export function useSaveOperationDetailsDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ operationId, input }: SaveOperationDetailsDraftArgs) => {
      const res = await operationsControllerSaveDetailsDraft(
        operationId,
        input as OperationDetailsDraftUpdateDto,
      );
      return expectData<{ draft_saved_at: string }>(res);
    },
    onMutate: async ({ operationId, input }) => {
      const key = [OPERATIONS_KEY, 'detail', operationId] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OperationDetail>(key);
      queryClient.setQueryData<OperationDetail>(key, (old) =>
        old
          ? {
              ...old,
              draft_kind: 'details',
              draft_details: input,
              draft_pre_flight_checks: null,
              draft_in_process_checks: null,
              draft_saved_at: new Date().toISOString(),
            }
          : old,
      );
      return { previous, key };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSuccess: (data, { operationId }) => {
      const key = [OPERATIONS_KEY, 'detail', operationId] as const;
      queryClient.setQueryData<OperationDetail>(key, (old) =>
        old ? { ...old, draft_saved_at: data.draft_saved_at } : old,
      );
    },
  });
}

// ── Stations the operation is enabled on ("Operation Enabled On") ────────

/**
 * Partner stations + per-station enablement for this operation. Wraps the
 * generated Orval query options to apply the quality data rules (see
 * packages/quality/CLAUDE.md). The generated query function returns the
 * `{ data, status, headers }` envelope, so consumers read the list via
 * `query.data?.data`.
 */
export function operationStationsQueryOptions(operationId: string | undefined) {
  return getOperationsControllerGetOperationStationsQueryOptions(operationId ?? '', {
    query: {
      enabled: !!operationId,
      staleTime: 15_000,
      placeholderData: keepPreviousData,
    },
  });
}

/** The cached `{ data, status, headers }` envelope for the stations query. */
export type OperationStationsEnvelope = { data: OperationStationDto[]; status: number };

// ── Stations autosave draft ──────────────────────────────────────────

interface SaveOperationStationsDraftArgs {
  operationId: string;
  input: { stations: OperationStationToggle[] };
}

/**
 * Debounced-autosave target for the Stations editor: persists straight to
 * `operations.draft_*` — the same reused ENG-764 draft slot the Checks draft
 * uses (see `useSaveOperationChecksDraft`), never touching the live
 * `operations_stations` junction until committed. Saving a Stations draft
 * supersedes any pending Checks draft, and vice versa (only one kind of draft
 * can be pending at a time — they share the same columns server-side).
 */
export function useSaveOperationStationsDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ operationId, input }: SaveOperationStationsDraftArgs) => {
      const res = await operationsControllerSaveStationsDraft(
        operationId,
        input as OperationStationsDraftUpdateDto,
      );
      return expectData<{ draft_saved_at: string }>(res);
    },
    onMutate: async ({ operationId, input }) => {
      const key = [OPERATIONS_KEY, 'detail', operationId] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OperationDetail>(key);
      queryClient.setQueryData<OperationDetail>(key, (old) =>
        old
          ? {
              ...old,
              draft_kind: 'stations',
              draft_stations: input.stations,
              draft_pre_flight_checks: null,
              draft_in_process_checks: null,
              draft_saved_at: new Date().toISOString(),
            }
          : old,
      );
      return { previous, key };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSuccess: (data, { operationId }) => {
      const key = [OPERATIONS_KEY, 'detail', operationId] as const;
      queryClient.setQueryData<OperationDetail>(key, (old) =>
        old ? { ...old, draft_saved_at: data.draft_saved_at } : old,
      );
    },
  });
}

/**
 * Commits a pending Stations draft: applies every staged toggle and clears
 * the draft (see `OperationsService.commitStationsDraft`). Invalidates the
 * operation detail (draft gone) and the live stations list (new enablement
 * state) — nothing else, since station enablement stays decoupled from Run
 * Sheet change control even when committed via a draft.
 */
export function useCommitOperationStationsDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (operationId: string) => {
      const res = await operationsControllerCommitStationsDraft(operationId);
      return expectData<{ applied: number }>(res);
    },
    onSuccess: (_data, operationId) => {
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'detail', operationId] });
      void queryClient.invalidateQueries({
        queryKey: getOperationsControllerGetOperationStationsQueryKey(operationId),
      });
    },
  });
}
